import React from 'react';

/* ─────────────────────────────────────────────────────────────────────
   isPaymentTermMet — shared helper (mirrors AdminProduction.js logic)
   Returns true if the order has met the advance-payment threshold
   required for production to be unlocked.
   Credit orders always return true.
───────────────────────────────────────────────────────────────────── */
export const isPaymentTermMet = (order, activeTerm) => {
  if (!order) return false;
  if (order.paymentType === 'credit') return true;
  if (!activeTerm) return true;
  const grandTotal     = Number(order.grandTotal)     || 0;
  const amountReceived = Number(order.amountReceived) || 0;
  const threshold  = activeTerm.threshold  != null ? Number(activeTerm.threshold)  : null;
  const minAmount  = activeTerm.minAmount  != null ? Number(activeTerm.minAmount)  : null;
  const flatAmount = activeTerm.flatAmount != null ? Number(activeTerm.flatAmount) : null;
  const paidPercent = grandTotal > 0 ? (amountReceived / grandTotal) * 100 : 100;
  if (minAmount !== null && grandTotal <= minAmount) return paidPercent >= 100;
  const percentRequired = threshold !== null ? (threshold / 100) * grandTotal : 0;
  const requiredAmount  = flatAmount !== null ? Math.max(flatAmount, percentRequired) : percentRequired;
  if (threshold === null) return true;
  return amountReceived >= requiredAmount;
};

/* ─────────────────────────────────────────────────────────────────────
   deriveSteps — compute which of the 4 stages are active for an order
───────────────────────────────────────────────────────────────────── */
const deriveSteps = (order, activeTerm) => {
  const status         = order.status || 'draft';
  const processEntries = Array.isArray(order.processEntries) ? order.processEntries : [];
  const readyEntries   = Array.isArray(order.readyEntries)   ? order.readyEntries   : [];

  const isCancelled = status === 'cancelled';

  const confirmedActive  = !isCancelled && status !== 'draft';
  const productionActive = confirmedActive && isPaymentTermMet(order, activeTerm);
  const readyActive      = productionActive && readyEntries.length > 0;
  const deliveredActive  = status === 'delivered';

  return [
    { key: 'confirmed',  label: 'Confirmed',  active: confirmedActive  },
    { key: 'production', label: 'Production', active: productionActive },
    { key: 'ready',      label: 'Ready',      active: readyActive      },
    { key: 'delivered',  label: 'Delivered',  active: deliveredActive  },
  ];
};

/* ─────────────────────────────────────────────────────────────────────
   OrderTrackingIndicator
   Props:
     order       — the sales order object (from API)
     activeTerm  — payment term object from /payment-terms/active (or null)
───────────────────────────────────────────────────────────────────── */
const OrderTrackingIndicator = ({ order, activeTerm }) => {
  if (!order) return null;

  const isCancelled = order.status === 'cancelled';
  const isDraft     = order.status === 'draft';

  if (isCancelled) {
    return (
      <div className="oti-root">
        <span className="oti-cancelled">Cancelled</span>
      </div>
    );
  }

  if (isDraft) {
    return (
      <div className="oti-root">
        <span className="oti-draft">Draft</span>
      </div>
    );
  }

  const steps = deriveSteps(order, activeTerm);

  return (
    <div className="oti-root" title="Order tracking status">
      {steps.map((step, idx) => (
        <React.Fragment key={step.key}>
          {/* Step dot + label */}
          <div className="oti-step">
            <div className={`oti-dot${step.active ? ' oti-dot--active' : ''}`}>
              {step.active && (
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <polyline
                    points="1.5,4 3.2,6 6.5,2"
                    stroke="#fff"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className={`oti-label${step.active ? ' oti-label--active' : ''}`}>
              {step.label}
            </span>
          </div>

          {/* Connector line between steps */}
          {idx < steps.length - 1 && (
            <div className={`oti-line${step.active ? ' oti-line--active' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default OrderTrackingIndicator;
